// Live cost between watch events (ui-spec §4.4): totalUSD + rate × elapsed, clamped to the object's limit and to the
// time its holds pay through. Server values always win; the extrapolated figure is marked with ≈.
import { formatMoney, formatRate, parseMicros } from "./format";

export interface CostStatus {
  totalUSD?: string;
  heldUSD?: string;
  rateUSDPerHour?: string;
  limitUSD?: string;
  final?: boolean;
  fundedUntilTime?: string;
}

export interface CostView {
  micros: number;
  approximate: boolean;
  final: boolean;
}

/** `asOf` is when the server wrote this status (the watch event time); `now` drives the tick. */
export function extrapolateCost(cost: CostStatus, asOf: Date, now: Date, tick = true): CostView {
  const total = parseMicros(cost.totalUSD) ?? 0;
  const rate = parseMicros(cost.rateUSDPerHour) ?? 0;
  if (cost.final || !tick || rate <= 0) return { micros: total, approximate: false, final: Boolean(cost.final) };
  let until = now.getTime();
  if (cost.fundedUntilTime) {
    const funded = new Date(cost.fundedUntilTime).getTime();
    if (!Number.isNaN(funded)) until = Math.min(until, funded);
  }
  const elapsedMs = Math.max(0, until - asOf.getTime());
  let micros = total + Math.floor((rate * elapsedMs) / 3_600_000);
  const limit = parseMicros(cost.limitUSD);
  if (limit !== undefined) micros = Math.min(micros, limit);
  return { micros, approximate: micros !== total, final: false };
}

/** The ticker's text: `≈$4.27`, `$3.12 ✓ final`, or `$0.00`. */
export function costLabel(view: CostView): string {
  const amount = formatMoney(view.micros / 1_000_000);
  if (view.final) return `${amount} ✓ final`;
  return view.approximate ? `≈${amount}` : amount;
}

/** Tooltip: "$4.27 so far · $2.85/h · held $0.95 · limit $40.00 · funded until 14:05". */
export function costTooltip(cost: CostStatus, clock: (t: string) => string): string {
  const parts = [`${formatMoney(cost.totalUSD)} so far`];
  if (cost.rateUSDPerHour && parseMicros(cost.rateUSDPerHour)) parts.push(formatRate(cost.rateUSDPerHour));
  if (cost.heldUSD && parseMicros(cost.heldUSD)) parts.push(`held ${formatMoney(cost.heldUSD)}`);
  if (cost.limitUSD) parts.push(`limit ${formatMoney(cost.limitUSD)}`);
  if (cost.fundedUntilTime) parts.push(`funded until ${clock(cost.fundedUntilTime)}`);
  return parts.join(" · ");
}
